import React from 'react';
import { ChevronLeft, LogOut, ShieldCheck, Building2, ExternalLink, Mail } from 'lucide-react';
import { Avatar, Badge, Button, ConfirmDialog } from '@parsina/framework';
import { C, soft, creditColor } from '@parsina/framework/tokens';
import { useTranslation, MIRROR_ICON } from '../i18n';
import { NAV_ITEMS } from '../constants/navItems.jsx';
import MobileSheet from './MobileSheet';
import { consumeOverlayEntry } from './useOverlayHistory';

/**
 * Everything the desktop sidebar carries that the four-item bar cannot.
 *
 * The sidebar is not only navigation: its foot is the ONLY place the panel
 * names the signed-in person, shows their پارسه balance, offers the account
 * app and signs them out, and its head is the clinic switcher. Folding the
 * overflow destinations into a sheet without those would quietly delete four
 * capabilities from the phone, so they live here in one surface, in the order
 * a person looks for them: who am I → where can I go → which clinic → out.
 *
 * The balance shown is the SIGNED-IN PERSON'S own — the only پارسه there is: the
 * clinic has no shared pool, and whoever acts pays. When the balance could not be
 * read the block is not rendered rather than showing a zero nobody can act on.
 *
 * See docs/MOBILE-REDESIGN.md §4.4.
 */
export default function MobileMoreSheet({
  open,
  onClose,
  /** Destination ids folded out of the bar, in nav order. */
  overflow = [],
  onNavigate,
  user,
  personalCredit,
  personalLevel,
  accountHref,
  creditsHref,
  memberships = [],
  activeRef,
  canSwitch,
  onSwitchClinic,
  pendingInvitations = [],
  onOpenAccess,
  onSignOut,
}) {
  const { t, formatNumber } = useTranslation();
  const [confirmOut, setConfirmOut] = React.useState(false);

  const byId = React.useMemo(() => new Map(NAV_ITEMS(t).map((n) => [n.id, n])), [t]);

  /**
   * Leave for a destination.
   *
   * The `consumeOverlayEntry()` is not optional and not tidiness: without it
   * this sheet's history entry is released a microtask after the router has
   * already pushed the destination, and the deferred `history.go(-1)` pops the
   * DESTINATION rather than the sheet — so every row here closed the menu and
   * left the operator exactly where they were. «بیماران», «همکاران» and
   * «کلینیک من» live behind this fold, so all three were unreachable from the
   * phone's navigation while each screen worked perfectly if you typed its URL.
   *
   * Consume, then close, then navigate with `replace` so the destination takes
   * the entry this sheet is giving up. See `useOverlayHistory.consumeOverlayEntry`.
   */
  const go = (id) => {
    consumeOverlayEntry();
    onClose();
    onNavigate(id, { replace: true });
  };

  return (
    <>
      <MobileSheet
        open={open}
        onClose={onClose}
        title={t('nav.mobile.moreTitle')}
        fullHeight
        snap={0.9}
        data-testid="mobile-more-sheet"
      >
        <div style={{ padding: '0.75rem 1rem 1.5rem', display: 'grid', gap: '1.25rem' }}>

          {/* ── Who am I ─────────────────────────────────────────────── */}
          <section aria-label={t('nav.mobile.account')} style={{ display: 'grid', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Avatar name={user?.name} size={44} src={user?.avatarUrl || undefined} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }} dir="auto">
                  {user?.name}
                </div>
                {user?.subtitle ? (
                  <div style={{ fontSize: '0.75rem', color: C.textSecondary }} dir="auto">
                    {user.subtitle}
                  </div>
                ) : null}
              </div>
            </div>

            {personalCredit ? (
              <a
                href={creditsHref}
                data-testid="mobile-more-credit"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.5rem', minHeight: '2.75rem',
                  padding: '0.5rem 0.75rem', borderRadius: '0.625rem',
                  background: soft(creditColor(personalLevel), 0.1),
                  color: C.textPrimary, textDecoration: 'none',
                }}
              >
                <span style={{ fontSize: '0.8125rem', color: C.textSecondary }}>
                  {t('nav.credits.label')}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <strong style={{ fontSize: '0.9375rem', color: creditColor(personalLevel) }}>
                    {formatNumber(personalCredit.balance)}
                  </strong>
                  <span style={{ fontSize: '0.75rem', color: C.textSecondary }}>
                    {t('nav.credits.unit')}
                  </span>
                </span>
              </a>
            ) : null}

            <Row
              icon={<ExternalLink size={18} />}
              label={t('nav.actions.account')}
              hint={t('nav.account.parsinaHint')}
              href={accountHref}
              testId="mobile-more-account"
            />
          </section>

          {/* ── Where can I go ───────────────────────────────────────── */}
          {(overflow.length > 0 || true) && (
            <section aria-label={t('nav.mobile.sections')} style={{ display: 'grid', gap: '0.25rem' }}>
              <SectionLabel>{t('nav.mobile.sections')}</SectionLabel>
              {overflow.map((id) => (
                <Row
                  key={id}
                  icon={byId.get(id)?.icon}
                  label={byId.get(id)?.label || id}
                  onClick={() => go(id)}
                  testId={`mobile-more-nav-${id}`}
                />
              ))}
              {/* No «تنظیمات» row. It used to be here because this sheet was the
                  only way in — the desktop panel has no sidebar row for it
                  either — and that is precisely what was wrong: a deprecated
                  screen kept alive by the one link that still pointed at it.
                  Everything a clinic operates from it is in «کلینیک من» or the
                  account app; the rest is definition work the scope ladder
                  keeps on the workstation. `/settings` on a phone now answers
                  with the desktop-only note rather than a surface. */}
              {pendingInvitations.length > 0 && (
                <Row
                  icon={<Mail size={18} />}
                  label={t('nav.items.invitations')}
                  badge={pendingInvitations.length}
                  onClick={() => go('invitations')}
                  testId="mobile-more-nav-invitations"
                />
              )}
            </section>
          )}

          {/* ── Which clinic ─────────────────────────────────────────── */}
          <section aria-label={t('nav.mobile.clinicSection')} style={{ display: 'grid', gap: '0.25rem' }}>
            <SectionLabel>{t('nav.mobile.clinicSection')}</SectionLabel>
            <Row
              icon={<ShieldCheck size={18} />}
              label={t('nav.mobile.myAccess')}
              onClick={() => { onClose(); onOpenAccess(); }}
              testId="mobile-more-access"
            />
            {canSwitch && memberships.map((m) => {
              const isActive = m.slug === activeRef || m.clinicId === activeRef;
              // A blocked membership stays VISIBLE and says why. Hiding it
              // answers "why can't I see my other clinic?" with silence.
              const blocked = !m.enterable;
              return (
                <Row
                  key={m.clinicId}
                  icon={<Building2 size={18} />}
                  label={m.clinicName}
                  hint={blocked ? t(`clinic.gate.blocked.${m.blockedReason}.title`) : m.city}
                  disabled={blocked || isActive}
                  trailing={isActive ? <Badge variant="success">{t('common.state.active')}</Badge> : null}
                  /* Consumes the entry for the same reason `go` does:
                     `switchClinic` navigates synchronously when no screen has
                     registered an unsaved-work guard, so without this the
                     sheet's deferred release popped the new clinic straight
                     back off and tapping the other clinic did nothing at all. */
                  onClick={() => { consumeOverlayEntry(); onClose(); onSwitchClinic(m); }}
                  testId={`mobile-more-clinic-${m.clinicId}`}
                />
              );
            })}
          </section>

          {/* ── Out ──────────────────────────────────────────────────── */}
          <Button
            variant="outline"
            onClick={() => setConfirmOut(true)}
            data-testid="mobile-more-signout"
            style={{ width: '100%', color: C.red, borderColor: soft(C.red, 0.35), minHeight: '2.75rem' }}
          >
            <LogOut size={16} style={{ marginInlineEnd: '0.5rem' }} aria-hidden="true" />
            {t('nav.actions.signOut')}
          </Button>
        </div>
      </MobileSheet>

      <ConfirmDialog
        open={confirmOut}
        onOpenChange={(v) => { if (!v) setConfirmOut(false); }}
        title={t('nav.actions.signOut')}
        description={t('auth.signOut.confirm')}
        confirmLabel={t('nav.actions.signOut')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => { setConfirmOut(false); onSignOut(); }}
      />
    </>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: '0.6875rem', fontWeight: 700, color: C.textMuted,
      padding: '0.25rem 0.25rem 0',
    }}>
      {children}
    </div>
  );
}

/** One ≥44px destination. A real element with a role, never a div with onClick. */
function Row({ icon, label, hint, badge, trailing, onClick, href, disabled, testId }) {
  const inner = (
    <>
      <span style={{ display: 'grid', placeItems: 'center', width: 22, color: C.textSecondary, flexShrink: 0 }}>
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
        <span style={{
          display: 'block', fontSize: '0.875rem', fontWeight: 600, color: C.textPrimary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} dir="auto">
          {label}
        </span>
        {hint ? (
          <span style={{ display: 'block', fontSize: '0.6875rem', color: C.textMuted }} dir="auto">
            {hint}
          </span>
        ) : null}
      </span>
      {badge != null ? <Badge variant="count">{badge}</Badge> : null}
      {trailing}
      {!trailing && !href ? (
        <ChevronLeft size={16} style={{ ...MIRROR_ICON, color: C.textMuted, flexShrink: 0 }} aria-hidden="true" />
      ) : null}
    </>
  );

  const style = {
    display: 'flex', alignItems: 'center', gap: '0.625rem',
    width: '100%', minHeight: '2.75rem',
    padding: '0.5rem 0.25rem',
    background: 'transparent', border: 'none', cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    textDecoration: 'none', fontFamily: 'inherit',
  };

  if (href) {
    return <a href={href} style={style} data-testid={testId}>{inner}</a>;
  }
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled || undefined}
      style={style}
      data-testid={testId}
    >
      {inner}
    </button>
  );
}
